import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

import { Session } from '../services/Session';
import { ToastService } from '../services/Toast.service';
import { TOAST_TYPE } from '../constants/Tpiq.constants';
import { environment } from '../../environments/environment';

/**
 * Post-login interstitial — shown to eligible users (feature-flag + Growth-tier
 * gated) so they can pick between continuing to Tax Planning (legacy dashboard)
 * or handing off to Tax Projections (new platform).
 *
 * The eligibility check itself lives in login.component.ts. By the time this
 * component renders, the user IS eligible — this component just renders the
 * two cards and routes on click.
 *
 * Visually mirrors the projections-side landing page at
 * taxplaniq-platform/frontend/src/app/features/landing/. SCSS uses hardcoded
 * hex values (legacy app lacks the projections design-token CSS variables).
 */
@Component({
  selector: 'app-choose-platform',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './choose-platform.component.html',
  styleUrl: './choose-platform.component.scss',
})
export class ChoosePlatformComponent {

  isRedirecting = false;

  constructor(
    private http: HttpClient,
    private router: Router,
    private toastService: ToastService,
  ) {}

  /** Tax Planning card — continue to the legacy dashboard. */
  goToTaxPlanning(): void {
    // If a Tax Projections handoff is in flight, ignore — the user is
    // already mid-redirect to projections; navigating to /tpiq/dashboard
    // here would race the cross-host redirect.
    if (this.isRedirecting) return;
    this.router.navigate(['/tpiq/dashboard']);
  }

  /** Tax Projections card — issue a one-time handoff token via legacy backend
   *  (HandoffOutController), then redirect to the projections frontend with
   *  the token in the URL. The projections /handoff component consumes it. */
  goToTaxProjections(): void {
    if (this.isRedirecting) return;
    this.isRedirecting = true;

    const userEmail = Session.getUser()?.id;
    // Multi-firm safety (CU-868jq2z15): real customers are single-tenant, so the
    // firm is unambiguous from the subdomain — derive firmId from the domain
    // rather than the stored Session.org. For the few internal multi-firm
    // accounts that log in via the generic app.taxplaniq.com URL there is no
    // firm in the domain, so fall back to Session.getOrg() (internal-only path).
    const host = window.location.hostname;
    const subdomain = host.split('.')[0];
    const firmFromDomain =
      host.endsWith('taxplaniq.com') && subdomain !== 'app' && subdomain !== 'stag-app'
        ? subdomain
        : null;
    const firmId = firmFromDomain ?? Session.getOrg();
    if (!userEmail || !firmId) {
      this.isRedirecting = false;
      this.toastService.pop(TOAST_TYPE.error, 'Missing user/firm context');
      return;
    }

    const url = environment.JAVA_BASE_URL +
      '/TaxPlanningServices/handoff-to-projections?userId=' + encodeURIComponent(userEmail) +
      '&firmId=' + encodeURIComponent(firmId);

    this.http.get<{ redirectUrl: string }>(url, {
      headers: { Authorization: 'Bearer ' + Session.getToken() }
    }).subscribe({
      next: (res) => {
        if (res?.redirectUrl) {
          window.location.assign(res.redirectUrl);
        } else {
          this.isRedirecting = false;
          this.toastService.pop(TOAST_TYPE.error, 'No redirect URL returned');
        }
      },
      error: () => {
        this.isRedirecting = false;
        this.toastService.pop(TOAST_TYPE.error, 'Tax Projections handoff failed');
      }
    });
  }
}
